import { useLocation } from "react-router-dom";
import { t } from "@/i18n-js/instance";
import {
  memberSettingsPaths,
  planSettingsPath,
} from "@/react/helpers/urlHelpers";
import { useBrandedAppsBuilderFeatureFlagUI } from "@/react/hooks/useBrandedAppsBuilderUIFeatureFlag";
import { useIsSitesEnabled } from "@circle-react/components/SettingsApp/Site/Pages/useIsSitesEnabled";
import { settingsNavPathV3 } from "@circle-react/helpers/urlHelpers/settingsNavPathV3";
import { useDevelopersAccessFlags } from "@circle-react/hooks/developers/useDevelopersAccessFlags";
import { separator } from "./separator";
import type { DropdownButton, DropdownLink, DropdownOption } from "./types";
import { adminIconsV3 } from "./useAdminIcons";

interface AdminOptionsProps {
  content: DropdownLink[];
  hasAIAgentsAccess?: boolean;
  isAdminDashboardEnabled?: boolean;
  isAffiliatesEnabled: boolean;
  isBannerVisible: boolean;
  isCommunityOnPlus: boolean;
  isWorkflowsEnabled: boolean;
  memberCount?: number;
  shortcutsModalButton: DropdownButton | null;
  showCustomizeTheme: boolean;
  toggleTheme: DropdownButton[];
  viewAs: DropdownButton;
}

export const useAdminOptions = ({
  memberCount,
  showCustomizeTheme,
  toggleTheme,
  viewAs,
  content,
  isCommunityOnPlus,
  isWorkflowsEnabled,
  isAffiliatesEnabled,
  isBannerVisible,
  shortcutsModalButton,
  hasAIAgentsAccess = true,
  isAdminDashboardEnabled = false,
}: AdminOptionsProps): DropdownOption[] => {
  const {
    adminDashboardIcon,
    membersIcon,
    marketingHubIcon,
    workflowsIcon,
    paywallsIcon,
    plansIcon,
    analyticsIcon,
    affiliatesIcon,
    settingsIcon,
    customizeThemeIcon,
    inviteMemberIcon,
    plusIcon,
    aiAgentsIcon,
    aiInboxIcon,
  } = adminIconsV3;
  const { isOverviewTabEnabled } = useDevelopersAccessFlags();

  const isSitesEnabled = useIsSitesEnabled();
  const { isBrandedAppsBuilderUIEnabled } =
    useBrandedAppsBuilderFeatureFlagUI();

  const { pathname } = useLocation();
  const options: DropdownOption[] = [];

  if (isAdminDashboardEnabled) {
    options.push({
      value: t("sidebar_admin_dashboard"),
      label: t("sidebar_admin_dashboard"),
      pathTo: settingsNavPathV3.admin_dashboard.index,
      icon: adminDashboardIcon,
      iconSize: 20,
    });
  }

  const membersOption: DropdownOption = {
    value: "members",
    label: t("section_audience"),
    pathTo: memberSettingsPaths.index(),
    icon: membersIcon,
    iconSize: 20,
  };

  if (memberCount) {
    membersOption.badge = String(memberCount);
  }

  options.push(membersOption);

  options.push(...content);

  options.push({
    value: t("sidebar_email"),
    label: t("sidebar_email"),
    pathTo: settingsNavPathV3.email.overview,
    icon: marketingHubIcon,
    iconSize: 20,
  });

  if (isWorkflowsEnabled) {
    options.push({
      value: t("sidebar_workflows"),
      label: t("sidebar_workflows"),
      pathTo: "/settings/workflows",
      icon: workflowsIcon,
      iconSize: 20,
    });
  }

  options.push({
    value: t("analytics.analytics"),
    label: t("analytics.analytics"),
    pathTo: "/settings/analytics",
    icon: analyticsIcon,
    iconSize: 20,
  });

  options.push({
    value: t("sidebar_ai_agents"),
    label: t("sidebar_ai_agents"),
    pathTo: "/settings/ai-agents/agents",
    icon: aiAgentsIcon,
    iconSize: 20,
  });

  if (hasAIAgentsAccess) {
    options.push({
      value: t("sidebar_ai_inbox"),
      label: t("sidebar_ai_inbox"),
      pathTo: "/settings/ai-inbox",
      icon: aiInboxIcon,
      iconSize: 20,
    });
  }

  options.push({
    value: t("sidebar_paywalls"),
    label: t("sidebar_paywalls"),
    pathTo: "/settings/paywalls",
    icon: paywallsIcon,
    iconSize: 20,
  });

  if (isAffiliatesEnabled) {
    options.push({
      value: t("sidebar_paywalls_affiliates"),
      label: t("sidebar_paywalls_affiliates"),
      pathTo: "/settings/affiliates_settings",
      icon: affiliatesIcon,
      iconSize: 20,
    });
  }

  // Determine if customer is on custom plan
  const isCustomerOnCustomPlan = !!isBrandedAppsBuilderUIEnabled;

  const getPlusOption = () => {
    // Non-Plus community option
    if (!isCommunityOnPlus) {
      return {
        value: t("pricing_plans.title"),
        label: t("settings.billing.plans.title"),
        pathTo: planSettingsPath(),
        icon: plansIcon,
        iconSize: 20,
      };
    }

    const commonProps = {
      value: t("pricing_plans.title"),
      label: t("settings_nav_v3.branded_apps_dashboard.title"),
      icon: plusIcon,
      iconSize: 20,
    };

    // Path depends on plan type
    const pathTo = isCustomerOnCustomPlan
      ? "/settings/plus"
      : "/settings/plus/lite";

    return {
      ...commonProps,
      pathTo,
    };
  };

  options.push(getPlusOption());

  if (!isCommunityOnPlus) {
    options.push({
      value: t("settings_nav_v3.settings.branded_app"),
      label: t("settings_nav_v3.settings.branded_app"),
      pathTo: "/settings/plus",
      icon: plusIcon,
      iconSize: 20,
    });
  }

  options.push({
    label: t("settings_nav_v3.site.label"),
    value: t("settings_nav_v3.site.label"),
    icon: "20-display",
    pathTo: settingsNavPathV3.site.index,
    iconSize: 20,
  });

  options.push({
    label: t("settings_nav_v3.developers.title"),
    value: t("settings_nav_v3.developers.title"),
    icon: "20-code-tag",
    pathTo: isOverviewTabEnabled
      ? settingsNavPathV3.developers.overview
      : settingsNavPathV3.developers.api,
    iconSize: 20,
  });

  options.push({
    value: t("sidebar_settings"),
    label: t("sidebar_settings"),
    pathTo: "/settings",
    icon: settingsIcon,
    iconSize: 20,
  });

  options.push(separator);

  if (isSitesEnabled) {
    options.push({
      value: t("settings_nav_v3.site_builder.label"),
      label: t("settings_nav_v3.site_builder.label"),
      pathTo: settingsNavPathV3.content.pages,
      icon: "20-display",
      iconSize: 20,
    });
  }

  if (showCustomizeTheme) {
    options.push({
      value: t("customize_theme.title"),
      label: t("customize_theme.title"),
      pathTo: "/settings/theme",
      icon: customizeThemeIcon,
      iconSize: 20,
    });
  }

  options.push(...toggleTheme);

  if (shortcutsModalButton) {
    options.push(shortcutsModalButton);
  }

  options.push(separator);

  if (!isBannerVisible) {
    options.push(viewAs);
  }

  options.push({
    value: t("sidebar_invite_member"),
    label: t("sidebar_invite_member"),
    pathTo: `${settingsNavPathV3.members.invite}?from=${pathname}`,
    icon: inviteMemberIcon,
    iconSize: 20,
  });

  return options;
};
